import React from "react";
import PropTypes from "prop-types";
import IconsWhatsapp from "../../assets/icons/ico-whatsapp.png";
import findStep from "../../screens/bot/stepModules/findStep";
import useInitialConfigStore from "../../storeModules/useInitialConfigStore";
import steps from "../../screens/bot/stepModules/steps";
import useUserStore from "../../storeModules/useUserStore";
import { metodosPagamentoEnum } from "../../utils/constants";
import preventDoubleClick from "../../utils/preventDoubleClick";

const createStepClassMapping = () => ({
  [findStep("retomar_conversa").index]: {
    1: "secondary",
  },
  [findStep("alterar_forma_pagamento").index]: {
    0: "primary",
    1: "primary",
    2: "primary",
    3: "btwhatsapp",
  },
  [findStep("pergunta_valor_renda").index]: {
    0: "primary",
    1: "primary",
    2: "primary",
    3: "btwhatsapp",
  },
  [findStep("sem_renda_extra").index]: { 0: "primary", 1: "btwhatsapp" },
  [findStep("pagamento_pendente").index]: { 0: "btwhatsapp" },
  [findStep("cartao_recusado").index]: {
    0: "primary",
    1: "primary",
    2: "primary",
    3: "btwhatsapp",
  },
  [findStep("erro_geral").index]: { 0: "primary", 1: "btwhatsapp" },
  [findStep("erro_proposta").index]: {
    0: "primary",
    1: "secondary",
    2: "btwhatsapp",
  },
  [findStep("termos_regulamento").index]: {
    1: "secondary",
    2: "secondary",
    3: "btwhatsapp",
  },
  [findStep("confirma_detalhes_plano").index]: {
    1: "secondary",
    2: "secondary",
    3: "tertiary",
  },
  [findStep("confirmacao_informacoes").index]: {
    1: "secondary",
    2: "secondary",
    3: "tertiary",
  },
  [findStep("gerando_pix").index]: {
    1: "primary",
    2: "secondary",
    3: "primary",
  },
  [findStep("opcoes_boleto").index]: {
    1: "primary",
    2: "secondary",
    3: "primary",
  },
  [findStep("formulario_cartao").index]: { 1: "secondary" },
  [findStep("pergunta_cupom").index]: { 0: "primary", 1: "primary" },
  [findStep("informa_codigo_cupom").index]: { 0: "primary" },
  [findStep("tentar_cupom_novamente").index]: { 0: "primary", 1: "primary" },
  [findStep("erro_cupom_opcoes").index]: { 0: "primary", 1: "primary" },
  [findStep("cupom_url_detectado").index]: { 0: "primary" },
});

const getClassName = (index, currentStep) => {
  const stepClassMapping = createStepClassMapping();
  return stepClassMapping[currentStep]?.[index] || "";
};

const isPaymentStep = (currentStep) => {
  return (
    currentStep === findStep("proposta_gerada").index ||
    currentStep === findStep("alterar_forma_pagamento").index
  );
};

const getCouponValidPaymentMethods = () => {
  const validMethods =
    useUserStore.getState().cupomData.formasPagamentosValidas;
  return validMethods.map((code) =>
    metodosPagamentoEnum[code] ? metodosPagamentoEnum[code] : ""
  );
};

const isCouponPaymentValid = (text, currentStep) => {
  const { cupomId } = useUserStore.getState().cupomData;

  if (!isPaymentStep(currentStep) || !cupomId) {
    return "";
  }

  const paymentMethodFromText = text.includes("CARTÃO") ? "CARTÃO" : text;
  const couponValidPaymentMethods = getCouponValidPaymentMethods();

  return !couponValidPaymentMethods.includes(paymentMethodFromText)
    ? "option-button--disabled"
    : "";
};

const hasInputCoupon = (currentStep) => {
  const { shouldUseCoupon } = useInitialConfigStore.getState();
  const { userCupom } = useUserStore.getState();

  const isCouponStep =
    currentStep === findStep("informa_codigo_cupom").index ||
    currentStep === findStep("tentar_cupom_novamente").index;

  return isCouponStep && shouldUseCoupon && !!userCupom
    ? "option-button--disabled"
    : "";
};

const isCouponButton = (currentStep, className) => {
  const currentStepName = steps[currentStep]?.name;
  return (
    (currentStepName === "informa_codigo_cupom" ||
      currentStepName === "tentar_cupom_novamente") &&
    className === "primary"
  );
};

const isWhatsAppButton = (className, optionText) => {
  return (
    className === "btwhatsapp" || optionText === "FALAR COM OS ESPECIALISTAS"
  );
};

const getOptionText = (option) => {
  return typeof option.text === "function" ? option.text() : option.text;
};

const getButtonStyles = (className, optionText) => {
  const { buttons } = useInitialConfigStore.getState().theme;

  const shouldApplyThemeColors =
    className !== "secondary" &&
    className !== "tertiary" &&
    optionText !== "FALAR COM OS ESPECIALISTAS";

  return {
    backgroundColor: shouldApplyThemeColors ? buttons.backgroundColor : "",
    color: shouldApplyThemeColors ? buttons.color : "",
  };
};

const handleButtonClick = (option, handleOptionClick) =>
  preventDoubleClick(async () => {
    await handleOptionClick(option);

    if (option.action) {
      option.action();
    }
  });

const WhatsAppIcon = () => (
  <figure className="image">
    <img alt="Atendimento via Whatsapp" className="Media" src={IconsWhatsapp} />
  </figure>
);

const OptionButton = ({ option, index, currentStep, handleOptionClick }) => {
  const mappingClass = getClassName(index, currentStep);
  const effectiveClass = option.className || mappingClass;
  const optionText = getOptionText(option);
  const isCouponBtn = isCouponButton(currentStep, effectiveClass);
  const isWhatsApp = isWhatsAppButton(effectiveClass, optionText);

  const buttonClasses = [
    "option-button",
    effectiveClass,
    optionText === "FALAR COM OS ESPECIALISTAS" ? "btwhatsapp" : "",
    isCouponPaymentValid(optionText, currentStep),
    hasInputCoupon(currentStep),
  ]
    .filter(Boolean)
    .join(" ");

  const buttonStyles = {
    ...getButtonStyles(effectiveClass, optionText),
    marginTop: isCouponBtn ? "10px" : "",
  };

  return (
    <button
      key={index}
      type="button"
      className={buttonClasses}
      style={buttonStyles}
      onClick={() => handleButtonClick(option, handleOptionClick)}
    >
      {isWhatsApp && <WhatsAppIcon />}
      <span>{optionText}</span>
    </button>
  );
};

OptionButton.propTypes = {
  option: PropTypes.shape({
    text: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
    action: PropTypes.func,
    className: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
  currentStep: PropTypes.number.isRequired,
  handleOptionClick: PropTypes.func.isRequired,
};

const Options = ({ options, currentStep, handleOptionClick }) => {
  const convertedOptions = Array.isArray(options) ? options : options();

  return convertedOptions.map((option, index) => (
    <OptionButton
      key={option.text}
      option={option}
      index={index}
      currentStep={currentStep}
      handleOptionClick={handleOptionClick}
    />
  ));
};

Options.propTypes = {
  options: PropTypes.oneOfType([PropTypes.array, PropTypes.func]).isRequired,
  currentStep: PropTypes.number.isRequired,
  handleOptionClick: PropTypes.func.isRequired,
};

export default Options;
